import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Package } from '@classifieds-sell/datalayer';
import { MotorsDraftFacade } from '@classifieds-sell/state';
import { EditedPhotoData, Photo, SelectedPhotoData } from '@classifieds-sell/trademe-core-ui';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MotorsSectionBase } from '../motors-section-base';
import { FooterService } from 'libs/datalayer/src';

@Component({
    selector: 'motors-description-and-photos-photo-section',
    templateUrl: './motors-description-and-photos-photo-section.component.html',
    styleUrls: ['./motors-description-and-photos-photo-section.component.scss'],
    standalone: false
})
export class MotorsDescriptionAndPhotosPhotoSectionComponent
    extends MotorsSectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('motorsDescriptionAndPhotosPhotoSectionForm')
    motorsDescriptionAndPhotosPhotoSectionForm: NgForm;

    photos: Photo[] = [];
    selectedPackage: Package;
    displayError = false;

    constructor(
        draftFacade: MotorsDraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _footerService: FooterService
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this.photos = this.draftData?.descriptionAndPhotos?.photos || [];
            this.selectedPackage = this.metadata.fees.find((i) =>
                i.id === this.draftData.listingDetails.categoryPackageId &&
                i.sellingType === this.draftData.listingDetails.sellingType);
            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        this.checkMinPhotoRequirement();

        if (this.displayError) {
            this.motorsDescriptionAndPhotosPhotoSectionForm?.form?.controls?.photos?.setErrors(
                { incorrect: true }
            );
        }

        return this.motorsDescriptionAndPhotosPhotoSectionForm.form.valid;
    }

    dirty(): boolean {
        if (this.photos.length === 0) {
            return true; // to update into db for listing with no photo
        }

        return this.motorsDescriptionAndPhotosPhotoSectionForm.form.dirty;
    }

    public onPhotosUploading(): void {
        this.motorsDescriptionAndPhotosPhotoSectionForm?.form.markAsDirty();
        this._footerService.setFooterVisibility(false);
    }

    public onPhotosUploaded(): void {
        this._footerService.setFooterVisibility(true);
    }

    onPhotoSelected(selectedPhotoData: SelectedPhotoData) {
        this.motorsDescriptionAndPhotosPhotoSectionForm?.form.markAsDirty(); // required to be able to save draft data
        this.draftFacade.addPhotoToDraftData(
            { photoId: uuidv4(), loading: true, hasError: false } as Photo,
            selectedPhotoData,
            'descriptionAndPhotos'
        );

        this.checkMinPhotoRequirement();
    }

    onPhotosUpdated(photos: Photo[]) {
        this.motorsDescriptionAndPhotosPhotoSectionForm?.form.markAsDirty(); // required to be able to save draft data
        this.draftFacade.updatePhotosInDraftData(
            photos,
            'descriptionAndPhotos'
        );

        this.checkMinPhotoRequirement();
    }

    onPhotoTransformed(editedPhotoData: EditedPhotoData) {
        this.motorsDescriptionAndPhotosPhotoSectionForm?.form.markAsDirty(); // required to be able to save draft data
        this.draftFacade.updateTransformPhotoDataToDraftData(
            editedPhotoData,
            'descriptionAndPhotos'
        );
    }

    checkMinPhotoRequirement(): void {
        const totalPhotos = this.photos.length;

        if (
            this.selectedPackage.minPhotoRequired === 0 ||
            this.selectedPackage.minPhotoRequired === null ||
            this.selectedPackage.minPhotoRequired <= totalPhotos
        ) {
            this.displayError = false;
        } else {
            this.displayError = true;
        }
    }
}
